// Panel desplegable de una carpeta de Mi Perfil (Fundamental o Brillamos). Réplica de
// ReconocimientoPage.tsx:1325-1387 del demo: header con nombre + cerrar, lista con avatar de
// iniciales, mensaje entre comillas, fecha e ícono del Fundamental, entrada escalonada.
// Los datos vienen YA CARGADOS por el llamador (PerfilView) — sin fetch propio.
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { RecognitionDto, FundamentalDto } from '@/api/recognition';
import { InitialsAvatar } from '@/components/common';
import { formatDate, nombreDelEmisor, RecognitionFundIcon } from './postitShared';

interface ProfileFolderPanelProps {
  title: string;
  items: RecognitionDto[];
  catalog: FundamentalDto[];
  lang: string;
  emptyText: string;
  onClose: () => void;
}

const STAGGER_DELAY_S = 0.04;

/** Panel expandido de una carpeta del perfil — desacoplado del muro de post-its de arriba. */
export function ProfileFolderPanel({ title, items, catalog, lang, emptyText, onClose }: Readonly<ProfileFolderPanelProps>) {
  const { t } = useTranslation('recognition');

  return (
    <motion.div
      className="rp2-folder-expanded"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 28 }}
    >
      <div className="rp2-folder-expanded-inner">
        <div className="rp2-folder-expanded-header">
          <span className="rp2-folder-expanded-title">{title}</span>
          <button type="button" className="rp2-folder-close" onClick={onClose} aria-label={t('common.close')}>
            <i className="fas fa-xmark" aria-hidden="true" />
          </button>
        </div>

        {items.length === 0 ? (
          <p className="rp2-folder-empty">{emptyText}</p>
        ) : (
          <div className="rp2-folder-items">
            {items.map((item, index) => {
              // T4 — defensa propia: "Anónimo" por flag o por ausencia de nombre (no solo por el backend).
              const emisorName = nombreDelEmisor(item, t);
              return (
                <motion.div
                  key={item.id}
                  className="rp2-folder-item"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * STAGGER_DELAY_S }}
                >
                  <div className="rp2-folder-item-left">
                    <InitialsAvatar name={emisorName} baseClass="rp2-folder-item-av" colorIndex={index} />
                    <div>
                      <span className="rp2-folder-item-from">{emisorName}</span>
                      <p className="rp2-folder-item-msg">&ldquo;{item.mensaje}&rdquo;</p>
                    </div>
                  </div>
                  <div className="rp2-folder-item-right">
                    <span className="rp2-folder-item-date">{formatDate(item.fecha, lang)}</span>
                    <RecognitionFundIcon
                      recognition={item}
                      catalog={catalog}
                      lang={lang}
                      iconClassName="rp2-folder-item-fund-icon"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default ProfileFolderPanel;
